/**
 * FirstLitterPromo
 * Pulsujący banner CTA na /panel widoczny tylko gdy:
 *   - user MA hodowlę
 *   - user NIE MA jeszcze ani jednego miotu
 *
 * Mini-stats (DB-driven, z `/api/panel/regional-demand`):
 *   "W [region]: [N] osób pytało o [breed] w ostatnich 7 dniach"
 * — to dramatycznie zwiększa motywację hodowcy.
 *
 * Auto-hide po pierwszym miocie.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight, TrendingUp } from 'lucide-react';
import { API } from '@/utils/api';

export default function FirstLitterPromo() {
  const [show, setShow] = useState(false);
  const [demand, setDemand] = useState(null);

  useEffect(() => {
    let alive = true;
    const token = localStorage.getItem('token') || localStorage.getItem('apisai_token') || '';
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/kennels`, { headers, timeout: 6000 }).catch(() => ({ data: [] })),
      axios.get(`${API}/litters`, { headers, timeout: 6000 }).catch(() => ({ data: [] })),
    ]).then(([kennelsRes, littersRes]) => {
      if (!alive) return;
      const kennels = Array.isArray(kennelsRes.data) ? kennelsRes.data : (kennelsRes.data?.kennels || []);
      const litters = Array.isArray(littersRes.data) ? littersRes.data : (littersRes.data?.litters || []);
      const shouldShow = kennels.length > 0 && litters.length === 0;
      setShow(shouldShow);
      if (shouldShow) {
        // Fetch demand stats only when banner widoczny — oszczędza request
        axios.get(`${API}/panel/regional-demand`, { headers, timeout: 6000 })
          .then(r => {
            if (alive && r.data?.available) setDemand(r.data);
          })
          .catch(() => { /* silent — banner i tak działa bez stats */ });
      }
    });
    return () => { alive = false; };
  }, []);

  if (!show) return null;

  // Build demand caption (gracefully degrades jeśli mało danych)
  const demandLine = (() => {
    if (!demand) return null;
    const total = (demand.intent_count || 0) + Math.floor((demand.view_count || 0) / 5);
    if (total < 3) return null; // nie pokazuj „2 zapytania" — słabo motywuje
    return {
      total,
      breed: demand.breed,
      region: demand.region,
    };
  })();

  return (
    <Link
      to="/wizard/miot"
      data-testid="first-litter-promo"
      className="block group relative overflow-hidden rounded-2xl border border-amber-300/40 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-rose-500/10 p-5 mb-6 hover:border-amber-300/70 transition"
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 bg-amber-400/20 blur-3xl rounded-full pointer-events-none animate-pulse" />
      <div className="relative flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/30">
          <Sparkles className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-300 mb-1">
            🔥 Następny krok
          </p>
          <h3 className="text-lg md:text-xl font-bold text-zinc-900 leading-tight">
            Dodaj pierwszy miot — to przyciąga klientów
          </h3>
          {demandLine ? (
            <p className="text-sm text-zinc-700 mt-1.5 inline-flex items-center gap-1.5" data-testid="first-litter-promo-demand">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
              <span>
                W <strong className="text-zinc-900">{demandLine.region}</strong>:{' '}
                <strong className="text-emerald-700">{demandLine.total}</strong> sygnałów zainteresowania{' '}
                rasą <strong>{demandLine.breed}</strong> w ostatnich 7 dniach
              </span>
            </p>
          ) : (
            <p className="text-sm text-zinc-600 mt-1">
              Hodowle z aktualnym miotem mają <strong>3× więcej zapytań</strong> niż te bez. Zajmie 5 minut.
            </p>
          )}
        </div>
        <div className="hidden sm:flex items-center gap-1 px-4 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-semibold whitespace-nowrap shrink-0 group-hover:bg-amber-500 group-hover:text-zinc-900 transition">
          ➕ Dodaj szczenięta
          <ArrowRight className="w-4 h-4 ml-0.5 group-hover:translate-x-1 transition" />
        </div>
      </div>
    </Link>
  );
}
